import { useEffect, useState } from "react";
import { format, addDays } from "date-fns";
import { CalendarIcon, Printer, Plus, Trash2, MessageCircle, AlertTriangle, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const ymd = (d) => format(d, "yyyy-MM-dd");
const party = (b) => `${b.adults}A${b.children ? ` ${b.children}C` : ""}`;
const waNumber = (p) => { const d = String(p).replace(/\D/g, ""); return d.length === 10 ? `91${d}` : d; };

function driverMessage(dep, trip, bookings) {
  const lines = bookings.filter((b) => trip.booking_ids.includes(b.id))
    .map((b) => `• ${b.booking_ref} ${b.guest_name} (${party(b)}) ${b.guest_mobile} — pickup: ${b.pickup_location || b.hotel_name || "ask guest"}${b.pickup_time ? ` at ${b.pickup_time}` : ""}${b.drop_location ? ` · drop: ${b.drop_location}` : ""}`);
  return `JawaiTrails trip\n${dep.slot.date} ${dep.slot.departure_time} ${dep.slot.label} · ${dep.safari_name}\nJeep: ${trip.jeep?.name} (${trip.jeep?.plate})\nGuests:\n${lines.join("\n") || "—"}\nPlease be at the first pickup 30 min before departure.`;
}

function TripDialog({ dep, trip, data, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ jeep_id: "", driver_id: "", booking_ids: [] });
  useEffect(() => {
    if (open) setForm(trip ? { jeep_id: trip.jeep_id, driver_id: trip.driver_id, booking_ids: trip.booking_ids } : { jeep_id: "", driver_id: "", booking_ids: [] });
  }, [open, trip]);
  const toggle = (id) => setForm((f) => ({ ...f, booking_ids: f.booking_ids.includes(id) ? f.booking_ids.filter((x) => x !== id) : [...f.booking_ids, id] }));
  const busyAt = (key) => new Set(data.departures.filter((d) => d.slot.departure_time === dep.slot.departure_time)
    .flatMap((d) => d.trips).filter((t) => t.id !== trip?.id).map((t) => t[key]));
  const busyJeeps = busyAt("jeep_id"), busyDrivers = busyAt("driver_id");
  const jeep = data.jeeps.find((j) => j.id === form.jeep_id);
  const seats = dep.bookings.filter((b) => form.booking_ids.includes(b.id))
    .reduce((t, b) => t + (b.booking_type === "private" ? Math.ceil((b.adults + b.children) / (b.vehicles || 1)) : b.adults + b.children), 0);

  const save = async () => {
    try {
      const body = { slot_id: dep.slot.id, ...form };
      if (trip) await api.patch(`/admin/dispatch/trips/${trip.id}`, body); else await api.post("/admin/dispatch/trips", body);
      toast.success("Trip saved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="trip-dialog">
        <DialogHeader><DialogTitle>{trip ? "Edit" : "Assign"} jeep · {dep.slot.departure_time} {dep.safari_name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="jt-eyebrow mb-1 block">Jeep</label>
            <Select value={form.jeep_id} onValueChange={(v) => setForm({ ...form, jeep_id: v })}>
              <SelectTrigger data-testid="trip-jeep"><SelectValue placeholder="Choose jeep"/></SelectTrigger>
              <SelectContent>{data.jeeps.map((j) => (
                <SelectItem key={j.id} value={j.id} disabled={j.status !== "active" || busyJeeps.has(j.id)}>
                  {j.name} · {j.seats} seats{j.status !== "active" ? ` (${j.status})` : busyJeeps.has(j.id) ? " (busy)" : ""}
                </SelectItem>))}</SelectContent>
            </Select></div>
          <div><label className="jt-eyebrow mb-1 block">Driver</label>
            <Select value={form.driver_id} onValueChange={(v) => setForm({ ...form, driver_id: v })}>
              <SelectTrigger data-testid="trip-driver"><SelectValue placeholder="Choose driver"/></SelectTrigger>
              <SelectContent>{data.drivers.map((d) => (
                <SelectItem key={d.id} value={d.id} disabled={d.status !== "active" || busyDrivers.has(d.id)}>
                  {d.name}{d.status !== "active" ? ` (${d.status})` : busyDrivers.has(d.id) ? " (busy)" : ""}
                </SelectItem>))}</SelectContent>
            </Select></div>
        </div>
        <div>
          <label className="jt-eyebrow mb-2 block">Bookings in this jeep</label>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {dep.bookings.map((b) => {
              const mine = trip?.booking_ids.includes(b.id);
              const full = b.jeeps_assigned >= b.jeeps_needed && !mine;
              return (
                <label key={b.id} className={`flex items-center gap-3 p-2 rounded-lg border ${form.booking_ids.includes(b.id) ? "border-[#1E3A2B] bg-[#F5F0EB]" : "border-[#E2DACF]"} ${full ? "opacity-40" : "cursor-pointer"}`} data-testid={`trip-booking-${b.booking_ref}`}>
                  <Checkbox checked={form.booking_ids.includes(b.id)} disabled={full} onCheckedChange={() => toggle(b.id)}/>
                  <div className="flex-1 text-sm">
                    <span className="font-mono text-[#1E3A2B]">{b.booking_ref}</span> · {b.guest_name} · {party(b)}
                    <div className="text-xs text-[#5C6B63]">{b.booking_type === "private" ? `Private · ${b.jeeps_assigned}/${b.jeeps_needed} jeeps` : "Shared"}{full ? " · fully assigned" : ""}</div>
                  </div>
                </label>
              );
            })}
          </div>
          {jeep && <p className={`text-xs mt-2 ${seats > jeep.seats ? "text-[#A33A2A] font-semibold" : "text-[#5C6B63]"}`} data-testid="trip-seat-count">{seats} / {jeep.seats} seats</p>}
        </div>
        <DialogFooter><button onClick={save} disabled={!form.jeep_id || !form.driver_id} className="jt-pill-primary text-sm disabled:opacity-40" data-testid="trip-save">Save trip</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Departure({ dep, data, reload }) {
  const [dlg, setDlg] = useState(null);
  const remove = async (t) => {
    if (!window.confirm("Remove this jeep assignment?")) return;
    try { await api.delete(`/admin/dispatch/trips/${t.id}`); reload(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <section className="bg-white rounded-2xl ring-1 ring-black/5 p-5 break-inside-avoid" data-testid={`dispatch-dep-${dep.slot.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-2xl text-[#1E3A2B]">{dep.slot.departure_time} <span className="text-base font-sans text-[#5C6B63]">{dep.slot.label}</span></div>
          <div className="font-serif text-lg">{dep.safari_name}</div>
        </div>
        <div className="text-right">
          {dep.unassigned > 0
            ? <span className="inline-flex items-center gap-1 text-sm text-[#A33A2A] font-semibold" data-testid={`dispatch-unassigned-${dep.slot.id}`}><AlertTriangle size={15}/>{dep.unassigned} jeep{dep.unassigned > 1 ? "s" : ""} to assign</span>
            : <span className="text-sm text-[#1E3A2B] font-semibold" data-testid={`dispatch-unassigned-${dep.slot.id}`}>All assigned</span>}
          <button onClick={() => setDlg({})} className="block ml-auto mt-2 text-sm text-[#C86D51] print:hidden" data-testid={`dispatch-add-trip-${dep.slot.id}`}><Plus size={14} className="inline"/> Assign jeep</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {dep.trips.map((t) => (
          <div key={t.id} className="rounded-xl bg-[#F5F0EB] p-4" data-testid={`dispatch-trip-${t.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-semibold">{t.jeep?.name} <span className="text-xs font-normal text-[#5C6B63]">{t.jeep?.plate}</span></div>
              <div className="flex gap-1 print:hidden">
                <a href={`https://wa.me/${waNumber(t.driver?.phone || "")}?text=${encodeURIComponent(driverMessage(dep, t, dep.bookings))}`} target="_blank" rel="noopener noreferrer" className="p-1.5 text-[#1E3A2B]" title="WhatsApp driver" data-testid={`dispatch-wa-${t.id}`}><MessageCircle size={16}/></a>
                <button onClick={() => setDlg({ trip: t })} className="p-1.5 text-xs underline" data-testid={`dispatch-edit-${t.id}`}>Edit</button>
                <button onClick={() => remove(t)} className="p-1.5 text-[#A33A2A]" data-testid={`dispatch-del-${t.id}`}><Trash2 size={15}/></button>
              </div>
            </div>
            <div className="text-sm flex items-center gap-1 mt-1"><Phone size={13}/>{t.driver?.name} · {t.driver?.phone}</div>
            <div className="text-xs text-[#5C6B63] mb-2">{t.guests} / {t.jeep?.seats} seats</div>
            <ul className="space-y-1.5 text-sm">
              {dep.bookings.filter((b) => t.booking_ids.includes(b.id)).map((b) => (
                <li key={b.id}><span className="font-mono text-xs text-[#1E3A2B]">{b.booking_ref}</span> {b.guest_name} · {party(b)} · {b.guest_mobile}
                  <div className="text-xs text-[#5C6B63] flex items-center gap-1" data-testid={`dispatch-pickup-${b.booking_ref}`}><MapPin size={11}/>{b.pickup_time && <strong className="text-[#1E3A2B]">{b.pickup_time}</strong>} {b.pickup_location || b.hotel_name || "Pickup not given"}{b.drop_location ? ` → drop ${b.drop_location}` : ""}</div></li>
              ))}
              {t.booking_ids.length === 0 && <li className="text-xs text-[#C86D51]">No guests in this jeep yet</li>}
            </ul>
          </div>
        ))}
      </div>

      {dep.bookings.some((b) => b.jeeps_assigned < b.jeeps_needed) && (
        <div className="mt-4 text-sm" data-testid={`dispatch-waiting-${dep.slot.id}`}>
          <div className="jt-eyebrow mb-1">Waiting for a jeep</div>
          {dep.bookings.filter((b) => b.jeeps_assigned < b.jeeps_needed).map((b) => (
            <div key={b.id} className="text-[#5C6B63]"><span className="font-mono text-xs text-[#1E3A2B]">{b.booking_ref}</span> {b.guest_name} · {party(b)} · {b.booking_type}{b.booking_type === "private" ? ` (${b.jeeps_needed - b.jeeps_assigned} more)` : ""} · {b.pickup_location || b.hotel_name || "—"}</div>
          ))}
        </div>
      )}
      {dlg && <TripDialog dep={dep} trip={dlg.trip} data={data} open onOpenChange={(o) => !o && setDlg(null)} onSaved={reload}/>}
    </section>
  );
}

export default function DispatchSheet({ initialDate }) {
  const [date, setDate] = useState(initialDate ? new Date(`${initialDate}T00:00:00`) : addDays(new Date(), 1));
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/dispatch", { params: { date: ymd(date) } }).then((r) => setData(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = data?.departures.reduce((t, d) => t + d.unassigned, 0) || 0;

  return (
    <div className="mt-6" data-testid="dispatch-sheet">
      <div className="flex items-center gap-3 flex-wrap mb-5 print:hidden">
        <Popover>
          <PopoverTrigger asChild>
            <button className="h-10 rounded-md border border-[#E2DACF] bg-white px-3 flex items-center gap-2 text-sm" data-testid="dispatch-date"><CalendarIcon size={15}/>{format(date, "EEE dd MMM yyyy")}</button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-auto" align="start"><Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)}/></PopoverContent>
        </Popover>
        <button onClick={() => setDate(addDays(date, -1))} className="text-sm underline" data-testid="dispatch-prev">← Prev</button>
        <button onClick={() => setDate(addDays(date, 1))} className="text-sm underline" data-testid="dispatch-next">Next →</button>
        {data && <span className={`text-sm ${total ? "text-[#A33A2A] font-semibold" : "text-[#1E3A2B]"}`} data-testid="dispatch-total-unassigned">{total ? `${total} jeep${total > 1 ? "s" : ""} still to assign` : "Everything assigned"}</span>}
        <button onClick={() => window.print()} className="jt-pill-outline text-sm py-2 ml-auto" data-testid="dispatch-print"><Printer size={15}/>Print sheet</button>
      </div>
      <h2 className="hidden print:block font-serif text-2xl mb-4">JawaiTrails dispatch · {format(date, "EEEE dd MMM yyyy")}</h2>
      <div className="space-y-5">
        {data?.departures.map((dep) => <Departure key={dep.slot.id} dep={dep} data={data} reload={load}/>)}
        {data && data.departures.length === 0 && <div className="bg-white rounded-xl p-8 text-center text-[#5C6B63]" data-testid="dispatch-empty">No confirmed safari bookings on this date.</div>}
      </div>
    </div>
  );
}
